import { ChangeEvent, FC, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { IconTrash } from '@robocorp/icons/iconic';

import type { JSONEditorProps } from '../types';

type PayloadEntry = [string, string | number];

const payloadToEntries = (payload: string): Array<PayloadEntry> => {
  try {
    const entries: Array<PayloadEntry> = Object.entries(JSON.parse(payload)).map(([k, v]) => {
      return [k, typeof v === 'string' || typeof v === 'number' ? v : JSON.stringify(v)];
    });

    if (entries[entries.length - 1][0]) {
      entries.push(['', '']);
    }

    return entries;
  } catch (_err) {
    return [['', '']];
  }
};

const entriesToPayload = (entries: Array<PayloadEntry>): string => {
  const values = entries
    // Filter out empty keys or values
    .filter(
      (entry) => !(entry[0].length === 0 && typeof entry[1] === 'string' && entry[1].length === 0),
    )
    // Try to parse entry value in case it's a valid JSON object
    .map((entry) => {
      const result = [...entry];

      try {
        const value = typeof entry[1] === 'string' ? JSON.parse(entry[1]) : entry[1];
        result[1] = value;
      } catch (_err) {
        return result;
      }

      return result;
    });
  return JSON.stringify(Object.fromEntries(values), null, 2);
};

const validPayload = (entries: Array<PayloadEntry>) => {
  const keys = entries.map(([key]) => key);
  const hasDuplicateKeys = new Set(keys).size !== keys.length;
  if (hasDuplicateKeys) {
    return false;
  }

  const hasEmptyKeys = entries.some(
    ([key, item]) => typeof item === 'string' && key.trim().length === 0 && item.trim().length > 0,
  );

  if (hasEmptyKeys) {
    return false;
  }

  return true;
};

const Container = styled.div`
  max-height: 200px;
  overflow: auto;
  padding: ${({ theme }) => theme.space.$4};
  margin: -${({ theme }) => theme.space.$4} 0 0 -${({ theme }) => theme.space.$4};
`;

export const SimpleEditor: FC<JSONEditorProps> = ({ value, onChange, setError }) => {
  const [entries, setEntries] = useState<Array<PayloadEntry>>([]);
  const { t } = useTranslation();

  useEffect(() => {
    setEntries(payloadToEntries(value));
  }, [value]);

  const onEntryChange = useCallback(
    (target: number, changingKey?: boolean) => (e: ChangeEvent<HTMLInputElement>) => {
      let newValue: string | number = e.target.value;

      try {
        const parsedValue = JSON.parse(e.target.value);
        if (typeof parsedValue === 'number') {
          newValue = parsedValue;
        }
      } catch (_err) {
        // Fallback to string value if not a number
      }

      const updatedData = [...entries];
      const entry = updatedData[target];
      updatedData[target] = changingKey ? [e.target.value, entry[1]] : [entry[0], newValue];

      if (updatedData[updatedData.length - 1][0]) {
        updatedData.push(['', '']);
      }

      setError?.(!validPayload(updatedData));
      setEntries(updatedData);
    },
    [setError, entries],
  );

  const onEntryRemove = useCallback(
    (target: number) => () => {
      const updatedData = entries.filter((_item, index) => target !== index);

      if (validPayload(entries)) {
        onChange(entriesToPayload(updatedData));
      }

      if (updatedData[updatedData.length - 1][0]) {
        updatedData.push(['', '']);
      }

      setEntries(updatedData);
    },
    [entries, onChange],
  );

  const onBlur = useCallback(() => {
    if (validPayload(entries)) {
      onChange(entriesToPayload(entries));
    }
  }, [entries, onChange]);

  const onKeyError = useCallback(
    (target: number) => {
      const entry = entries[target];
      const key = entry[0].trim();
      const item = typeof entry[1] === 'number' ? entry[1].toString() : entry[1].trim();

      if (key.length === 0 && item.length > 0) {
        return t('jsonEditor.emptyKeyError');
      }

      if (key.length > 0 && entries.findIndex(([curr]) => curr === key) < target) {
        return t('jsonEditor.duplicateKeyError');
      }

      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries],
  );

  return (
    <Container>
      <DataGrid columns={['auto', 'auto', 'min-content']}>
        {entries.map(([key, item], index) => (
          // eslint-disable-next-line react/no-array-index-key
          <DataGrid.Row key={index}>
            <DataGrid.Input
              aria-label="Entry Key"
              value={key}
              placeholder="Key"
              error={onKeyError(index)}
              onChange={onEntryChange(index, true)}
              onBlur={onBlur}
            />
            <DataGrid.Input
              aria-label="Entry value"
              value={item}
              placeholder="Value"
              onChange={onEntryChange(index, false)}
              onBlur={onBlur}
            />
            {entries.length > 1 && !key && <DataGrid.Cell />}
            {entries.length > 1 && key && (
              <DataGrid.Button
                type="button"
                onClick={onEntryRemove(index)}
                icon={IconTrash}
                aria-label="Delete entry"
              />
            )}
          </DataGrid.Row>
        ))}
      </DataGrid>
    </Container>
  );
};
